import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Grid2,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import ShareIcon from '@mui/icons-material/Share';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactPlayer from 'react-player';
import { collection, getDocs, getDoc,doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { auth } from '../firebase/firebase';

const CoursePage = () => {
  const { courseName } = useParams();
  const [course, setCourse] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [completedVideos, setCompletedVideos] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourse = async () => {
      const coursesCollection = collection(db, 'courses');
      const courseSnapshot = await getDocs(coursesCollection);
      const courseList = courseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const selectedCourse = courseList.find(course => course.name.toLowerCase().replace(/\s+/g, '-') === courseName);
      setCourse(selectedCourse);
      
      // Check if the user is already enrolled
      const user = auth.currentUser;
      if (user) {
        const enrollmentDocRef = doc(db, "userEnrollments", user.uid, "courses", courseName);
        const enrollmentSnapshot = await getDoc(enrollmentDocRef);
        if (enrollmentSnapshot.exists()) {
          setIsEnrolled(true);
          
          // Fetch completed videos for the user
          const progressDocRef = doc(db, "userProgress", user.uid, "courses", courseName);
          const progressSnapshot = await getDoc(progressDocRef);
          if (progressSnapshot.exists()) {
            setCompletedVideos(progressSnapshot.data().completedVideos || {});
          }
        }
      }
    };
    fetchCourse();
  }, [courseName]);

  const handleEnroll = async () => {
    const user = auth.currentUser;
    if (user) {
      const enrollmentDocRef = doc(db, "userEnrollments", user.uid, "courses", courseName);
      await setDoc(enrollmentDocRef, { enrolled: true }, { merge: true });
      setIsEnrolled(true);
    }
    navigate(`/course/${courseName}/content`); 
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleTakeTest = () => {
    navigate(`/course/${course.name.replace(/\s+/g, '-').toLowerCase()}/test`);
  };

  if (!course) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress />
        <Typography variant="h5" sx={{ mt: 3, textAlign: 'center' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Course Title */}
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
        {course.name}
      </Typography>

      {/* Course Description */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Description</Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        {course.description}
      </Typography>

      <Grid2 container spacing={2}>
        <Grid2>
          <Button 
            variant="contained" 
            color={isEnrolled ? "success" : "primary"} 
            onClick={handleEnroll}
          >
            {isEnrolled ? "Enrolled / Continue Course" : "Enroll Now"}
          </Button>
          <Button variant="outlined" onClick={handleTakeTest} sx={{ ml: 2 }}>Take Test</Button>
        </Grid2>
        <Grid2>
          <IconButton color="primary" aria-label="share" onClick={() => alert("Share functionality coming soon!")}>
            <ShareIcon />
          </IconButton>
        </Grid2>
      </Grid2>
      
      {/* Instructor and Price */}
      <Card sx={{ mt: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="h6">Instructor: {course.author}</Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>Price: {course.price}</Typography>
        </CardContent>
      </Card>

      {/* Curriculum Section */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>Curriculum</Typography>
      {course.curriculum && course.curriculum.map((topic, mainIndex) => (
        <Accordion
          key={mainIndex}
          expanded={expanded === mainIndex}
          onChange={handleAccordionChange(mainIndex)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{topic.mainTopic}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {topic.subParts.map((part, subIndex) => (
              <Box key={subIndex} sx={{ mb: 2 }}>
                <Typography variant="body1">{part.title}</Typography>
                {part.content.map((content, contentIndex) => {
                  const key = `${mainIndex}-${subIndex}-${contentIndex}`;
                  return (
                    <Box key={contentIndex} sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body2" color={completedVideos[key] ? "green" : "text.secondary"}>
                        {completedVideos[key] ? "Completed" : "Not Completed"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Demo Video */}
      {course.videoUrl && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            Course Preview
          </Typography>
          <ReactPlayer
            url={course.videoUrl}
            controls
            width="100%"
            height="500px"
          />
        </Box>
      )}
    </Container>
  );
};

export default CoursePage;
